// Import necessary dependencies and components
import React, { useMemo, useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import {
  Row,
  Col,
  Button,
  ButtonGroup,
  Dropdown,
  Badge,
  Nav,
} from "react-bootstrap";
import ClickBack from "../../../components/buttons/ClickBack";


import IconShow  from "../../../images/fi_eye.png";
import IconEdit  from "../../../images/edit.png";
import Download from "../../../images/download.svg";

import { fetchAllFournisseurCommandes, fetchAllFournisseurCommandesForCalendar, fetchAvailableDeliveryDates } from "../CommandesFournisseurs/DataService";
import { urlPdf } from "../../../utils/ApiConfig";
import { format } from "date-fns";
import DynamicTable from "../../../components/DynamicTable";
import SearchBar from "../../../components/SearchBar";
import ElementsPerPageDropdown from "../Component/ElementsPerPageDropdown";
import CommandeStatusDropdown from "../Component/CommandeStatusDropdown";
import LivreurDropdown from "../Component/LivreurDropdown";
import ClientTypeDropdown from "../Component/ClientTypeDropdown";
import DeliveryDatePicker from "../Component/DeliveryDatePicker";
import KanbanView from "./KanbanView";
import CalendarView from "./CalendarView";



// Define the functional component
function ListCommandesAllFournisseur(props) {
  // State to hold user data
  const [commandesData, setCommandesData] = useState(null);
  const [calendarData, setCalendarData] = useState(null); // Données séparées pour le calendrier
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [nbr, setNbr] = useState(1);
  const [nbElement, setNbElement] = useState(10);

  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedLivreur, setSelectedLivreur] = useState("");
  const [selectedClientType, setSelectedClientType] = useState("");
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState(null);
  const [availableDeliveryDates, setAvailableDeliveryDates] = useState([]);
  const [enRetard, setEnRetard] = useState(false);

  // Gestion de la vue active (liste, kanban, calendrier)
  const [activeView, setActiveView] = useState(() => {
    return localStorage.getItem('commandesView') || 'list';
  });

  const handleViewChange = (view) => {
    setActiveView(view);
    localStorage.setItem('commandesView', view);
  };

  const handleStatusChange = (newStatus) => {
        setSelectedStatus(newStatus);
        setNbr(1); // reset to first page
        };

  const handleLivreurChange = (newLivreur) => {
        setSelectedLivreur(newLivreur);
        setNbr(1); // reset to first page
        };

  const handleClientTypeChange = (newClientType) => {
        setSelectedClientType(newClientType);
        setNbr(1); // reset to first page
        };

  const handleDeliveryDateChange = (date) => {
        setSelectedDeliveryDate(date);
        setNbr(1); // reset to first page
        };

  const handleEnRetardChange = (event) => {
        setEnRetard(event.target.checked);
        setNbr(1); // reset to first page
        };

  const handleOrderStatusChange = async (orderId, newStatus) => {
    try {
      // TODO: Implement API call to update order status
      // For now, just refetch the data to show the change
      // You'll need to add the actual API endpoint here
      console.log(`Updating order ${orderId} to status ${newStatus}`);

      // Refetch data after status change
      await fetchData();
    } catch (error) {
      console.error("Error updating order status:", error);
    }
  };


  // Cette fonction pour afficher liste des fournisseurs
  const fetchData = async () => {
    try {
      setLoading(true);
      const formattedDate = selectedDeliveryDate ? format(selectedDeliveryDate, "yyyy-MM-dd") : null;
      const data = await fetchAllFournisseurCommandes(nbr, searchTerm, nbElement, selectedStatus, selectedLivreur, formattedDate, enRetard, selectedClientType);
      setCommandesData(data);
    } catch (error) {
      console.error("Error lors de affiche liste fournisseur:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour charger toutes les commandes pour le calendrier (sans pagination)
  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const formattedDate = selectedDeliveryDate ? format(selectedDeliveryDate, "yyyy-MM-dd") : null;
      // Utiliser l'endpoint dédié au calendrier qui retourne toutes les commandes
      const data = await fetchAllFournisseurCommandesForCalendar(searchTerm, selectedStatus, selectedLivreur, formattedDate, enRetard, selectedClientType);
      setCalendarData(data);
    } catch (error) {
      console.error("Error lors de affiche calendrier:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all available delivery dates (independent of pagination)
  const fetchDeliveryDates = async () => {
    try {
      const dates = await fetchAvailableDeliveryDates();
      setAvailableDeliveryDates(dates);
    } catch (error) {
      console.error("Error fetching delivery dates:", error);
    }
  };
    const handleNombreElementParPage = (nbElement) => {
      setNbElement(nbElement); // Update the state with the selected value
    };
    // Update search term and fetch data
    const handleSearch = (term) => {
      setSearchTerm(term);
      setNbr(1); // Reset to first page
    };
  // useEffect pour charger automatiquement les données lors du montage du composant
  useEffect(() => {
    fetchData(nbr); // Toujours charger pour liste/kanban/total
  }, [nbr, searchTerm, nbElement, selectedStatus, selectedLivreur, selectedDeliveryDate, enRetard, selectedClientType]);

  // useEffect pour charger les données du calendrier quand on passe en vue calendrier
  useEffect(() => {
    if (activeView === 'calendar') {
      fetchCalendarData();
    }
  }, [activeView, searchTerm, selectedStatus, selectedLivreur, selectedDeliveryDate, enRetard, selectedClientType]);

  // useEffect pour charger les dates de livraison disponibles une seule fois
  useEffect(() => {
    fetchDeliveryDates();
  }, []); // Run only once on component mount

  // Colonnes mémorisées pour le composant DynamicTable
  const columns = useMemo(() => {
    return [
      {
        Header: "Id",
        accessor: "id",
        Cell: ({ value }) => <span>{value}</span>,
        style: { width: "100px", minWidth: "80px", maxWidth: "120px", textAlign: "center" },
      },
      {
        Header: "Type",
        accessor: "client_type",
        Cell: ({ value }) => {
          let badgeClass = "bg-secondary";
          let icon = "";
          let label = "";

          switch (value) {
            case "fournisseur":
              badgeClass = "bg-primary";
              icon = "🏢";
              label = "Fournisseur";
              break;
            case "particulier":
              badgeClass = "bg-success";
              icon = "👤";
              label = "Particulier";
              break;
            case "entreprise":
              badgeClass = "bg-warning text-dark";
              icon = "💼";
              label = "Entreprise";
              break;
            default:
              badgeClass = "bg-secondary";
              icon = "❓";
              label = value || "Inconnu";
          }

          return (
            <Badge className={badgeClass}>
              {icon} {label}
            </Badge>
          );
        },
        style: { width: "130px", minWidth: "120px", maxWidth: "150px" },
      },
      {
        Header: "Date et Heure de livraison",
        accessor: "DateLivraison",
        Cell: ({ value }) => (
          <span>
            {value ? format(new Date(value), "dd-MM-yyyy HH:mm") : ""}
          </span>
        ),
      },
      {
        Header: "Client",
        accessor: "fournisseur_profil.user_profil.RaisonSocial",
        Cell: ({ row }) => {
          const clientType = row.original.client_type;
          const raisonSocial = row.original.fournisseur_profil?.user_profil?.RaisonSocial;
          const nom = row.original.NomClient;
          const prenom = row.original.PrenomClient;

          // Si c'est un fournisseur, afficher la raison sociale
          if (clientType === 'fournisseur') {
            return <span>{raisonSocial || '—'}</span>;
          }

          // Pour les particuliers et entreprises, afficher Nom + Prénom
          const fullName = [nom, prenom].filter(Boolean).join(' ');
          return <span>{fullName || raisonSocial || '—'}</span>;
        },
      },
      
      //{ Header: "N° de bon de livraison", accessor: "NBonLivraison" },
      // { Header: "N° de téléphone client", accessor: "TelClient" },
      /**{
        Header: "Date et Heure de commande",
        accessor: "DateCommande",
        Cell: ({ value }) => (
          <span>
            {value ? format(new Date(value), "dd-MM-yyyy HH:mm") : ""}
          </span>
        ),
      },*/
      //{ Header: " Raison Social", accessor: "fournisseur_profil.user_profil.RaisonSocial" },
//      { Header: "Montant de la commande", accessor: "MontantCommande"},
      {
        Header: "Adresse pick-up",
        accessor: "AdressePickUp",
        Cell: ({ value }) => {
          const ville = value?.split(",")[1]?.trim(); // extrait "Antibes"
          return <span>{ville || "—"}</span>;
        },
      },
      {
        Header: "Adresse de livraison",
        accessor: "AdresseLivraison",
        Cell: ({ value }) => {
          const ville = value?.split(",")[1]?.trim(); // extrait "Antibes"
          return <span>{ville || "—"}</span>;
        },
      },
      {
        Header: "Tarif livraison",
        accessor: "livraisonHT",
        Cell: ({ value }) => <span>{value ? `${value} €` : "--"}</span>,
      },
      {
        Header: "Nom du Livreur",
        accessor: "livreur_profil",
        Cell: ({ value }) => {
          const nom = value?.nom;
          const prenom = value?.prenom;
          return <span>{nom || prenom ? `${nom || ""} ${prenom || ""}`.trim() : "--"}</span>;
        },
      },
      
      
  
      // {Header: "Adresse mail fournisseur",accessor: "emailFournisseur" },
       /** {
        Header: "Statut de commande",
        accessor: "StatuCommande",
        Cell: ({ value }) => {
          let statusLabel = "";
          let badgeClass = "bg-secondary"; // Default badge color
      
          switch (value) {
            case 1:
              statusLabel = "En cours de livraison";
              badgeClass = "bg-warning";
              break;
            case 2:
              statusLabel = "Livré";
              badgeClass = "bg-success";
              break;
            case 3:
              statusLabel = "En cours de préparation";
              badgeClass = "bg-info";
              break;
            case 6:
              statusLabel = "Annulé";
              badgeClass = "bg-danger";
              break;
            default:
              statusLabel = "En cours de préparation";
              badgeClass = "bg-info";
          }
      
          return (
            <Badge className={badgeClass}>
              {statusLabel}
            </Badge>
          );
        }
      },   
      {
        Header: "Statut de paiement",
        accessor: "StatuPaiement",
        Cell: ({ value }) => {
          let statusLabel = "";
          let badgeClass = "bg-secondary"; // Default badge color
      
          switch (value) {
            case 1:
              statusLabel = "Facturé";
              badgeClass = "bg-success";
              break;
            case 2:
              statusLabel = "Non facturé";
              badgeClass = "bg-danger";
              break;
            default:
              statusLabel = "Non facturé";
              badgeClass = "bg-danger";
          }
      
          return (
            <Badge className={badgeClass}>
              {statusLabel}
            </Badge>
          );
        }
      }, **/  
      {
        Header: "Actions",
        Cell: ({ value, row }) => (
          <div>
            <a href={`/commande-fournisseur/show/${row.original.id}`} className="p-2">
              <img src={IconShow} width="20px" alt="Voir" />
            </a>
            <a
              href={`/commande-fournisseur/edit/${row.original.id}`}
              className="p-2"
            >
              <img src={IconEdit} width="20px" alt="Modifier" />
            </a>
            <a href={`${urlPdf}/Bordereau/${row?.original.Bordereau}`} target="_blank" rel="Bordereaux">
                <img src={Download} alt="Download"  />
            </a>
          </div>
        ),
      },
    ].filter(Boolean); // Filter out any falsey values
  }, [nbr]); // Add dependencies if needed

  // Render loading message while data is being fetched
  if (!commandesData) {
    return (
      <Container>
        <p>Chargement...</p>
      </Container>
    );
  }

  // Render the component
  return (
    <Container>
      <div className="commandesFournisseursList">
        {/* Breadcrumb navigation */}
        <p className="h6">Accueil / Livraisons </p>

        {/* Header and filters */}
        <Row className="pt-4 pb-2">
          <Col style={{ display: "flex", alignItems: "center" }}>
            <ClickBack />
            {/* Display dynamic title */}
            <h1 className="p-1">Livraisons</h1>
          </Col>
          {/* add button */}
          <Col
            className="d-flex justify-content-end align-items-center"
            style={{ alignContent: "right" }}
          >
            <a className="btn btn-primary" href="/commande-fournisseur/add">
              Ajouter Livraison
            </a>
          </Col>
        </Row>

        {/* Tabs de sélection de vue */}
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link
              active={activeView === 'list'}
              onClick={() => handleViewChange('list')}
              style={{ cursor: 'pointer' }}
            >
              📋 Liste
              {commandesData?.total && <Badge bg="secondary" className="ms-2">{commandesData.total}</Badge>}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={activeView === 'kanban'}
              onClick={() => handleViewChange('kanban')}
              style={{ cursor: 'pointer' }}
            >
              📊 Kanban
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={activeView === 'calendar'}
              onClick={() => handleViewChange('calendar')}
              style={{ cursor: 'pointer' }}
            >
              📅 Calendrier
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {/* Filtres (affichés pour toutes les vues) */}
        <Row className="pt-4 pb-2 align-items-center">
        <Col xs="auto">
	  <ElementsPerPageDropdown
              nbElement={nbElement}
              onNbElementChange={handleNombreElementParPage}
            />
	</Col>

	   <Col xs="auto">
            <CommandeStatusDropdown
              selectedStatus={selectedStatus}
              onStatusChange={handleStatusChange}
            />
          </Col>

          <Col xs="auto">
            <LivreurDropdown
              selectedLivreur={selectedLivreur}
              onLivreurChange={handleLivreurChange}
            />
          </Col>

          <Col xs="auto">
            <ClientTypeDropdown
              selectedClientType={selectedClientType}
              onClientTypeChange={handleClientTypeChange}
            />
          </Col>

          <DeliveryDatePicker
            selectedDate={selectedDeliveryDate}
            onDateChange={handleDeliveryDateChange}
            availableDates={availableDeliveryDates}
          />

          <Col xs="auto" className="d-flex align-items-center">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="enRetardCheckbox"
                checked={enRetard}
                onChange={handleEnRetardChange}
              />
              <label className="form-check-label" htmlFor="enRetardCheckbox">
                En retard
              </label>
            </div>
          </Col>

          <Col
            xs
            lg="2"
            className="d-flex justify-content-end align-items-center"
            style={{ alignContent: "right" }}
          ></Col>
        </Row>
         <SearchBar searchTerm={searchTerm} handleSearch={handleSearch} />

        {/* Affichage conditionnel selon la vue active */}
        {loading ? (
          // Display loader while data is being fetched
          <p className="lead">Chargement...</p>
        ) : (
          <>
            {/* Vue Liste */}
            {activeView === 'list' && (
              <DynamicTable
                columns={columns}
                data={commandesData?.data}
                link={commandesData?.links}
                last_page={commandesData?.last_page}
                setNbr={setNbr}
                nbr={nbr}
                total={commandesData?.total}
                name="CommandesAllfournisseurs"
              />
            )}

            {/* Vue Kanban */}
            {activeView === 'kanban' && (
              <KanbanView
                orders={commandesData?.data || []}
                onStatusChange={handleOrderStatusChange}
              />
            )}

            {/* Vue Calendrier */}
            {activeView === 'calendar' && (
              <CalendarView
                orders={calendarData?.data || []}
                onEventClick={(order) => {
                  window.location.href = `/commande-fournisseur/show/${order.id}`;
                }}
              />
            )}
          </>
        )}
      </div>
    </Container>
  );
}

// Export the component as the default export
export default ListCommandesAllFournisseur;
